import { useMemo, useState } from "react";
import { Button } from "#/components/common/Button";
import { Card } from "#/components/common/Card";
import { CheckboxField } from "#/components/common/CheckboxField";
import { TextArea } from "#/components/common/TextArea";
import { TextInput } from "#/components/common/TextInput";
import type { PermissionOption, RoleFormValues } from "../types";

type RoleFormProps = {
  defaultValues?: Partial<RoleFormValues>;
  permissions: PermissionOption[];
  isSubmitting?: boolean;
  onSubmit: (values: RoleFormValues) => Promise<void> | void;
};

export function RoleForm({
  defaultValues,
  permissions,
  isSubmitting = false,
  onSubmit,
}: RoleFormProps) {
  const [values, setValues] = useState<RoleFormValues>({
    name: defaultValues?.name ?? "",
    code: defaultValues?.code ?? "",
    description: defaultValues?.description ?? "",
    active: defaultValues?.active ?? true,
    permissionCodes: defaultValues?.permissionCodes ?? [],
  });

  const [error, setError] = useState<string | null>(null);

  const groupedPermissions = useMemo(() => {
    const groups = new Map<string, PermissionOption[]>();

    permissions.forEach((permission) => {
      const key = permission.code.includes("_")
        ? permission.code.split("_")[0]
        : "GENERAL";

      if (!groups.has(key)) {
        groups.set(key, []);
      }
      groups.get(key)?.push(permission);
    });

    return Array.from(groups.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [permissions]);

  function update<K extends keyof RoleFormValues>(
    key: K,
    value: RoleFormValues[K],
  ) {
    setValues((prev) => ({ ...prev, [key]: value }));
  }

  function togglePermission(code: string) {
    setValues((prev) => {
      const exists = prev.permissionCodes.includes(code);
      return {
        ...prev,
        permissionCodes: exists
          ? prev.permissionCodes.filter((item) => item !== code)
          : [...prev.permissionCodes, code],
      };
    });
  }

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);

    if (!values.name.trim()) {
      setError("Role name is required.");
      return;
    }

    if (!values.code.trim()) {
      setError("Role code is required.");
      return;
    }

    try {
      await onSubmit({
        ...values,
        name: values.name.trim(),
        code: values.code.trim().toUpperCase(),
        description: values.description.trim(),
      });
    } catch (err: any) {
      setError(
        err?.response?.data?.message ?? err?.message ?? "Failed to save role.",
      );
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <Card className="p-6">
        <div className="grid gap-4 md:grid-cols-2">
          <TextInput
            label="Role name"
            value={values.name}
            onChange={(e) => update("name", e.target.value)}
            placeholder="e.g. HR Manager"
            required
          />

          <TextInput
            label="Role code"
            value={values.code}
            onChange={(e) => update("code", e.target.value)}
            placeholder="e.g. HR_MANAGER"
            required
          />

          <div className="md:col-span-2">
            <TextArea
              label="Description"
              value={values.description}
              onChange={(e) => update("description", e.target.value)}
              placeholder="Describe what this role is used for"
              rows={4}
            />
          </div>

          <CheckboxField
            label="Role is active"
            checked={values.active}
            onChange={(checked) => update("active", checked)}
          />
        </div>
      </Card>

      <Card className="p-6">
        <div className="mb-4">
          <h2 className="text-lg font-semibold text-slate-900">Permissions</h2>
          <p className="text-sm text-slate-500">
            Choose the permissions assigned to this role.
          </p>
        </div>

        <div className="space-y-6">
          {groupedPermissions.map(([group, items]) => (
            <div key={group} className="space-y-3">
              <div className="border-b border-slate-200 pb-2">
                <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-700">
                  {group}
                </h3>
              </div>

              <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                {items.map((permission) => (
                  <label
                    key={permission.id}
                    className="flex cursor-pointer items-start gap-3 rounded-2xl border border-slate-200 bg-white p-4 transition hover:border-emerald-300 hover:shadow-sm"
                  >
                    <input
                      type="checkbox"
                      className="mt-1 h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                      checked={values.permissionCodes.includes(permission.code)}
                      onChange={() => togglePermission(permission.code)}
                    />

                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-slate-900">
                        {permission.name || permission.code}
                      </p>
                      <p className="mt-1 text-xs text-slate-500">
                        {permission.code}
                      </p>
                      {permission.description ? (
                        <p className="mt-2 text-xs text-slate-500">
                          {permission.description}
                        </p>
                      ) : null}
                    </div>
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Card>

      {error ? (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      <div className="flex flex-wrap items-center gap-3">
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? "Saving..." : "Save role"}
        </Button>
      </div>
    </form>
  );
}
